/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { HeroGlowFragment } from "@cosmos/hygraph-schema";

import {
	BlurredImage,
	OptimizedCosmosAssetFragment,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { css, cx } from "@panda/css";

const styled = css({
	position: "relative",
	w: "full",
});

const glowWrap = css({
	position: "absolute",
	height: "256px",
	transform: "translateY(-56px)",
	w: "65%",
	right: "eds.dimension.128",
	zIndex: 0,

	xxlDown: {
		w: "75%",
		right: "eds.dimension.64",
	},

	xlDown: {
		w: "85%",
	},

	xsOnly: {
		right: "eds.dimension.24",
	},
});

const narrowStyles = css({
	maxWidth: `cosmos.article.sm`,
	marginInline: "auto",
	boxSizing: "content-box",
});

type HeroGlowProps = {
	glowImageLarge?: boolean;
} & Pick<HeroGlowFragment, "blurredImage" | "title" | "desc" | "narrow">;

const glowImageLargeStyles = css({
	minH: "700px",
});

export const HeroGlow = ({ blurredImage, title, desc, narrow, glowImageLarge }: HeroGlowProps): React.JSX.Element => {
	const width = 256;
	const height = 144;
	const dimensions = { width, height };

	//check that either title and desc are valid and have content besides whitespace
	const isValidHeading = Boolean(/\S/.test(title?.text || "") || /\S/.test(desc || ""));

	return (
		<div className={cx(`hero-glow`, styled)}>
			<div className={cx(`hero-glow-wrap`, glowWrap, glowImageLarge ? glowImageLargeStyles : "")}>
				<BlurredImage
					image={{ ...blurredImage, ...dimensions } as OptimizedCosmosAssetFragment}
					blurPixels={200}
					distortionStrength={5}
					opacity={0.5}
					{...dimensions}
				/>
			</div>
			{isValidHeading ? (
				<ResponsiveContainer>
					<SectionContainer className={cx(narrow && narrowStyles)} disablePaddingBlockEnd>
						<SectionHeading title={{ ...title, as: "h1" }} desc={desc} />
					</SectionContainer>
				</ResponsiveContainer>
			) : null}
		</div>
	);
};
